(ns io.daskul.webapp.hooks.list-params
  "Pure filter/sort/URL-param logic for the list hooks — no React/uix/re-frame
   deps, so it runs under the :node-test unit build.

   Filters flagged :active-context (F-041, e.g. branch-id) are derived from
   app state, not user-chosen, and must never round-trip through the URL
   (SMS-238) — a stale value left in the address bar would otherwise win over
   a freshly switched active context."
  (:require
   [clojure.string :as str]))

(defn build-dispatch-params
  "Build query params for dispatch, removing nil/blank/empty values."
  [params]
  (->> params
       (remove (fn [[_ v]]
                 (or (nil? v)
                     (and (string? v) (str/blank? v))
                     (and (coll? v) (empty? v)))))
       (into {})))

(defn filter-value->param
  "Convert a filter value to a query param string.
   Vector values are comma-joined for IN queries: [\"a\" \"b\"] → \"a,b\"
   nil means 'All' (remove the key)."
  [v]
  (cond
    (nil? v) nil
    (vector? v) (str/join "," v)
    :else v))

(defn date->iso-string
  "Convert a JS Date to ISO date string (YYYY-MM-DD), or nil."
  [date]
  (when date
    (let [y (.getFullYear date)
          m (inc (.getMonth date))
          d (.getDate date)]
      (str y "-" (when (< m 10) "0") m "-" (when (< d 10) "0") d))))

;; ============================================================================
;; Segments
;; ============================================================================

(defn find-segment-by-key
  "Find a segment map by its :key within a filter config's :segments."
  [filter-cfg segment-key]
  (some #(when (= (:key %) segment-key) %) (:segments filter-cfg)))

(defn default-segment-key
  "The segment key a list starts on: an explicit :default, else the first
   declared segment.

   Falling back to the *first segment* rather than the literal \"all\" matters
   for any aggregate that doesn't offer an All segment (#324): a dead key
   matches no option, leaving the segmented control with nothing selected.
   Every config that leads with an All segment is unaffected — its first key
   is \"all\" anyway. nil when nothing is declared."
  [filter-cfg]
  (or (:default filter-cfg)
      (:key (first (:segments filter-cfg)))))

(defn find-segment-key-for-param
  "Given a filter config and a URL param value string, find the matching
   segment key. nil when no segment matches — the caller surfaces those as a
   'Filtered by' escape hatch rather than silently mis-selecting a segment."
  [filter-cfg param-value]
  (some (fn [seg]
          (when (= param-value (filter-value->param (:value seg)))
            (:key seg)))
        (:segments filter-cfg)))

(defn- segmented-filters
  [filter-config]
  (filter #(= :segmented (:widget %)) filter-config))

(defn resolve-segment-param
  "Resolve a `?segment=<key>` URL param against the filter config.

   Deep-links name a *segment key*, never the states behind it, so the
   aggregate definition stays the single source of truth for stage groupings
   (#324) — a link to the Trial stage doesn't have to restate
   `trial-scheduled,no-show,trial-attended` and then rot when the grouping
   changes.

   Resolves against whichever segmented filter declares the key; a list may
   have more than one (e.g. academic periods segments on both :state and
   :period-type). Returns {:field f :value param-or-nil}, or nil for an
   unknown key so the caller falls through to the defaults."
  [filter-config segment-key]
  (when segment-key
    (some (fn [f]
            (when-let [seg (find-segment-by-key f segment-key)]
              {:field (:field f) :value (filter-value->param (:value seg))}))
          (segmented-filters filter-config))))

(defn segment-count
  "Compute count for a segment by summing raw counts for its values.

   A segment with :value nil means 'All' — uses an explicit :all key in
   counts if the counts-fn provides one (segments that aren't mutually
   exclusive, e.g. independent EXISTS checks, can't be summed without
   double-counting), otherwise sums every value (correct when segments
   are mutually exclusive, e.g. GROUP BY a single-state column).

   A grouped segment sums its states, so the badge on a stage matches the
   stage: Trial = trial-scheduled + no-show + trial-attended (#324). The
   backend counts endpoint groups by the raw column and knows nothing about
   the grouping — see leads/counts_test.clj."
  [counts segment]
  (when counts
    (let [values (:value segment)]
      (if (nil? values)
        (if (contains? counts :all)
          (:all counts)
          (reduce + 0 (vals counts)))
        ;; Specific values — sum matching
        (reduce (fn [acc v] (+ acc (get counts (keyword v) 0)))
                0 values)))))

;; ============================================================================
;; Empty state
;; ============================================================================

(defn empty-message
  "Copy for a list with no rows.

   With filters or a search active, the useful advice is to relax them — that
   copy is label-free and reads correctly everywhere. Otherwise the message is
   derived from the aggregate's plural label, which only reads as a sentence
   while that label is a countable plural: #324 renamed Leads to \"Pipeline\"
   and the derivation produced \"No pipeline found.\" (#625). `configured` is
   `:ui :list :empty-message`, the opt-out."
  [{:keys [filtered? label configured]}]
  (cond
    filtered? "No matching records found. Try adjusting your search or filters."
    (not (str/blank? configured)) configured
    :else (str "No " (str/lower-case (or label "records")) " found.")))

(defn segment-data-with-counts
  "Build SegmentedControl :data, folding each segment's count into its label.
   A segment whose count hasn't loaded yet renders its bare label rather than
   \"(0)\", so an unloaded bar never reads as an empty pipeline."
  [segments counts]
  (mapv (fn [s]
          (let [cnt (segment-count counts s)]
            {:value (:key s)
             :label (if (some? cnt) (str (:label s) " (" cnt ")") (:label s))}))
        segments))

(defn- resolve-segment-default
  "The default filter value contributed by a segmented filter config: the
   :value of its default segment, as a param string. nil when that segment is
   an 'All' (nil :value) or isn't found."
  [filter-cfg]
  (some-> (find-segment-by-key filter-cfg (default-segment-key filter-cfg))
          :value
          filter-value->param))

(defn- segment-defaults
  "Extract default filter values from segmented filter configs.
   Returns a map of {field-keyword param-string}."
  [filter-config]
  (->> (segmented-filters filter-config)
       (reduce (fn [acc {:keys [field] :as f}]
                 (if-let [param (resolve-segment-default f)]
                   (assoc acc field param)
                   acc))
               {})))

(defn- active-context-defaults
  "Extract auto-filter defaults from active context (F-041).
   For filters with :active-context property, resolves value from context map.
   Returns a map of {field-keyword value-string}."
  [filter-config ctx]
  (when ctx
    (->> filter-config
         (keep (fn [f]
                 (when-let [ctx-key (:active-context f)]
                   (when-let [v (get ctx ctx-key)]
                     [(:field f) (str v)]))))
         (into {}))))

(defn default-filter-values
  "Compute combined filter defaults: segmented defaults + active-context auto-filters.
   Active-context defaults override segmented defaults when both are present."
  [filter-config active-context]
  (merge (segment-defaults filter-config)
         (active-context-defaults filter-config active-context)))

(defn active-context-fields
  "Fields flagged :active-context in filter-config (e.g. branch-id)."
  [filter-config]
  (->> filter-config (filter :active-context) (map :field) set))

(defn sort->param
  "Convert sort vector to query param string.
   [[:name :asc]] → \"name:asc\"
   [[:name :asc] [:created-at :desc]] → \"name:asc,created-at:desc\"
   nil → nil"
  [sort-vec]
  (when (seq sort-vec)
    (->> sort-vec
         (map (fn [[field dir]] (str (name field) ":" (name dir))))
         (str/join ","))))

(defn- param->sort
  "Parse sort param string back to sort vector.
   \"name:asc\" → [[:name :asc]]
   \"name:asc,created-at:desc\" → [[:name :asc] [:created-at :desc]]
   nil → nil"
  [s]
  (when (and s (not (str/blank? s)))
    (->> (str/split s #",")
         (mapv (fn [part]
                 (let [[field dir] (str/split part #":")]
                   [(keyword field) (keyword dir)]))))))

(defn keywordize-map
  "Keywordize string keys in a map (reitit query params come as string keys)."
  [m]
  (when m
    (reduce-kv (fn [acc k v]
                 (assoc acc (if (string? k) (keyword k) k) v))
               {} m)))

(defn parse-url-params
  "Parse URL query params into structured state.
   Returns {:filters {field-kw param-str} :q string :sort sort-vec :page int}.
   Fields in ac-fields (active-context filters, e.g. branch-id) are stripped
   from the raw params before merging over defaults, so a stale or
   hand-edited URL param can never override the active context (SMS-238).

   With a filter-config, a `?segment=<key>` param resolves to that segment's
   filter value (#324); an 'All' segment clears the field rather than
   filtering on nil, and an unknown key leaves the defaults alone. :segment is
   always stripped — it addresses a filter, it isn't one."
  ([raw-params defaults ac-fields]
   (parse-url-params raw-params defaults ac-fields nil))
  ([raw-params defaults ac-fields filter-config]
   (let [params (apply dissoc (keywordize-map raw-params) ac-fields)
         q (:q params)
         sort-vec (param->sort (:sort params))
         page (when-let [p (:page params)]
                (if (string? p) (js/parseInt p 10) p))
         segment (resolve-segment-param filter-config (:segment params))
         ;; Everything except :q, :sort, :page, :page-size, :segment is a filter
         filters (merge defaults (dissoc params :q :sort :page :page-size :segment))
         filters (if-let [{:keys [field value]} segment]
                   (if value
                     (assoc filters field value)
                     (dissoc filters field))
                   filters)]
     {:filters filters
      :q (or q "")
      :sort sort-vec
      :page (when (and page (> page 1)) page)})))

(defn build-url-params
  "Build URL query params from current state. Always includes filter values
   so the URL reflects what the user sees (including defaults). Fields in
   ac-fields (active-context filters, e.g. branch-id) are derived from app
   state and are excluded — they must never round-trip through the URL
   (SMS-238)."
  [filters query sort-vec page ac-fields default-sort]
  (let [;; Include all non-empty filter values, excluding active-context ones
        url-filters (build-dispatch-params (apply dissoc filters ac-fields))
        ;; Build params map
        params (cond-> url-filters
                 (and query (not (str/blank? query)))
                 (assoc :q query)

                 (and sort-vec (not= sort-vec default-sort))
                 (assoc :sort (sort->param sort-vec))

                 (and page (> page 1))
                 (assoc :page (str page)))]
    (when (seq params) params)))
